// api/salaryApi.js
import axios from "axios";

const BASE_URL = axios.create({
  baseURL: `${process.env.REACT_APP_SOCKET_URL}/api/salary`,
});

const getHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

// ── Generate Salary (single staff) ────────────────────────────────────────
// POST /api/salary/generate
// payload: { staffId, month, bonus, bonusNote, remarks, overrides }
// NOTE: grossSalary is NOT needed — auto-fetched from staff.baseSalary on backend
export async function generateSalary(payload) {
  try {
    const res = await BASE_URL.post(`/generate`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Bulk Generate Salary ──────────────────────────────────────────────────
// POST /api/salary/bulk-generate
// payload: { month, staffIds?: [...] }
// If staffIds omitted → generates for ALL active staff with baseSalary set
// Attendance is auto-pulled from the previous month
export async function bulkGenerateSalary(payload) {
  try {
    const res = await BASE_URL.post(`/bulk-generate`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Get All Salaries (paginated + filtered) ───────────────────────────────
export async function getAllSalaries(payload) {
  try {
    const {
      month = "", orgId = "", zoneId = "", status = "",
      c_search_term = "", n_page = 1, n_limit = 20,
    } = payload || {};
    const res = await BASE_URL.get(`/`, {
      ...getHeaders(),
      params: { month, orgId, zoneId, status, c_search_term, n_page, n_limit },
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Get Single Salary by ID ────────────────────────────────────────────────
export async function getSalaryById(id) {
  try {
    const res = await BASE_URL.get(`/${id}`, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Get Salary History for a Staff ────────────────────────────────────────
export async function getStaffSalaryHistory(staffId, payload) {
  try {
    const { n_page = 1, n_limit = 12 } = payload || {};
    const res = await BASE_URL.get(`/staff/${staffId}`, {
      ...getHeaders(),
      params: { n_page, n_limit },
    });
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Update Salary (bonus, deductions, remarks) ────────────────────────────
export async function updateSalary(id, payload) {
  try {
    const res = await BASE_URL.put(`/update/${id}`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Approve Salary ─────────────────────────────────────────────────────────
export async function approveSalary(id) {
  try {
    const res = await BASE_URL.patch(`/approve/${id}`, {}, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Bulk Approve ───────────────────────────────────────────────────────────
export async function bulkApproveSalary(payload) {
  try {
    const res = await BASE_URL.patch(`/bulk-approve`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Mark Salary Paid ───────────────────────────────────────────────────────
export async function markSalaryPaid(id, payload) {
  try {
    const res = await BASE_URL.patch(`/mark-paid/${id}`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Bulk Mark Paid ─────────────────────────────────────────────────────────
export async function bulkMarkPaid(payload) {
  try {
    const res = await BASE_URL.patch(`/bulk-mark-paid`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Toggle Hold ────────────────────────────────────────────────────────────
export async function toggleHold(id, payload) {
  try {
    const res = await BASE_URL.patch(`/hold/${id}`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Delete Salary ─────────────────────────────────────────────────────────
export async function deleteSalary(id) {
  try {
    const res = await BASE_URL.delete(`/${id}`, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Download Salary Slip (PDF) ─────────────────────────────────────────────
export async function downloadSalarySlip(id, filename) {
  try {
    const res = await BASE_URL.get(`/slip/${id}/download`, {
      ...getHeaders(),
      responseType: "blob",
    });
    const url  = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename || "Salary-Slip.pdf");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { success: true };
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Email Salary Slip ───────────────────────────────────────────────────────
export async function emailSalarySlip(id, payload = {}) {
  try {
    const res = await BASE_URL.post(`/slip/${id}/email`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}

// ── Bulk Email Salary Slips ──────────────────────────────────────────────────
export async function bulkEmailSlips(payload) {
  try {
    const res = await BASE_URL.post(`/slip/bulk-email`, payload, getHeaders());
    return res.data;
  } catch (err) {
    return { success: false, error: err.response?.data?.message || err.message };
  }
}